import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, OnInit, ChangeDetectorRef, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { ModalPdfComponent } from '../modal-pdf/modal-pdf.component';
import { RateAnswerComponent } from "../../views/technician/rate-answer/rate-answer.component";
import { jsPDF } from "jspdf";
import { Store } from '@ngrx/store';
import { EquipmentState, EquipmentDetails } from '../../../store/equipment/equipment.model';
import { LocalForageUtilities} from "../../../utilities/browser/localForage.utilities";

import * as pdfjsLib from 'pdfjs-dist';

import { ButtonComponent } from '../button/button.component';
import { HttpRequestService } from '../../../utilities/http/request.utilities';
import { VideoService } from '../../../services/video/video.service';
import { MarkdownComponent } from 'ngx-markdown';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.min.mjs';

@Component({
  selector: 'app-chat-reply',
  standalone: true,
  imports: [CommonModule, MatCardModule, MatIconModule, MatDividerModule, MatButtonModule, MatProgressSpinnerModule, RateAnswerComponent, ButtonComponent, MarkdownComponent],
  templateUrl: './chat-reply.component.html',
  styleUrls: ['./chat-reply.component.css']
})
export class ChatReplyComponent implements OnChanges, OnInit {
  @Input() reply: string | null = null;
  @Input() videoAnswerText: string = '';
  @Input() resources: { title: string, url: string, message: string, downloadUrl: string, page?: number }[] = [];
  @Input() videoResources: { title: string, docPath: string, message: string, offset: string }[] = [];
  public videoLoadingStates: boolean[] = [];
  @Input() message: string = '';
  @Input() transactionId: string = '';
  @Input() safetyText: string = '';
  @Input() safetyGenericText: string = '';
  @Input() showGeneralAIFallback: boolean = false;
  @Input() genericReply: string | null = null;
  @Input() isGenericLoading: boolean = false;
  isLoading: boolean = false;

  @ViewChild('genericReplyContainer') genericReplyContainer!: ElementRef<HTMLElement>;

  @Output() onClick: EventEmitter<void> = new EventEmitter<void>();
  @Output() onTryGeneralAI: EventEmitter<void> = new EventEmitter<void>();

  public equipmentDetails: EquipmentDetails = {
    id: '',
    make: '',
    model: '',
    serialNumberRange: '',
    pvc: '',
    equipmentCode: ''
  };

  constructor(private dialog: MatDialog, private httpRequestService: HttpRequestService, private store: Store<{ equipment: EquipmentState }>, private cdRef: ChangeDetectorRef, private localForage: LocalForageUtilities, private videoService: VideoService) {}

  ngOnInit(): void {
    this.store.select(state => state.equipment.equipment.details).subscribe(details => {
      this.equipmentDetails = details || this.equipmentDetails;
    });
  }

  // Add a class property to cache download URLs
  private downloadUrlCache: Map<string, string> = new Map();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['resources'] && changes['resources'].currentValue.length !== 0) {
      const uniqueMap = new Map<string, boolean>();

      this.resources = this.resources.filter(resource => {
        const key = `${resource.message.trim()}|${resource.page}`;
        if (uniqueMap.has(key)) return false;
        uniqueMap.set(key, true);
        return true;
      });

      const cloudfrontMap = new Map<string, string>();
      this.resources.forEach(resource => {
        if (resource.url?.includes('cloudfront.net')) {
          cloudfrontMap.set(resource.title, resource.url);
        }
      });

      this.resources.forEach(resource => {
        if (!resource.url && cloudfrontMap.has(resource.title)) {
          const propagatedUrl = cloudfrontMap.get(resource.title)!;
          resource.url = propagatedUrl;
          resource.downloadUrl = propagatedUrl;
        }
      });

      this.resources.forEach((resource, index) => {
        const downloadUrl = resource.downloadUrl;

        if (this.downloadUrlCache.has(downloadUrl)) {
          const cached = this.downloadUrlCache.get(downloadUrl)!;
          this.resources[index].url = cached;
          this.resources[index].downloadUrl = cached;
        } else {
          const previousResources = changes['resources'].previousValue || [];
          const alreadyFetched = previousResources.some((prev: any) => prev.downloadUrl === downloadUrl);

          if (!alreadyFetched) {
            this.fetchDownloadUrl(downloadUrl).then(({ downloadUrl: resolved }) => {
              this.downloadUrlCache.set(downloadUrl, resolved);
              this.resources[index].url = resolved;
              this.resources[index].downloadUrl = resolved;
            }).catch(error => {
              console.warn('Failed to fetch CloudFront URL:', error);
            });
          }
        }
      });
      const pageMap = new Map<number | string, { 
        messages: string[], 
        resource: { title: string, url: string, message: string, downloadUrl: string, page?: number } 
      }>();
  
      this.resources.forEach(resource => {
        const pageKey = resource.page ?? 'unknown';
        if (!pageMap.has(pageKey)) {
          pageMap.set(pageKey, { messages: [resource.message], resource });
        } else {
          pageMap.get(pageKey)!.messages.push(resource.message);
        }
      });
  
      this.resources = Array.from(pageMap.values()).map(({ messages, resource }) => {
        return {
          ...resource,
          message: messages.join(' \n ') 
        };
      });
    }
  }

  private fetchDownloadUrl(providerResourceUrl: string): Promise<{ downloadUrl: string }> {
    const sanitizedUrl = providerResourceUrl.replace(/;/g, '%3B');
    return new Promise((resolve, reject) => {
      this.httpRequestService.request<any>('GET', sanitizedUrl).subscribe({
        next: (response) => resolve({ downloadUrl: response.download_url }),
        error: (error) => reject(error)
      });
    });
  }

  // Always pass the page value directly - it could be a label or number
  // ModalPdfComponent will handle page label resolution
  openUrl(url: string, title: string, page?: string | number): void {
    this.dialog.open(ModalPdfComponent, {
      data: { pdfUrl: url, title: title, page: page }
    });
  }

  async openVideo(docPath: string, index: number, offset?: string): Promise<void> {
    this.videoLoadingStates[index] = true;
    this.cdRef.detectChanges();
    try {
      let presignedUrl = await this.videoService.getPresignedUrl(docPath);
      
      // Parse offset to extract start time in seconds (format: HH:MM:SS-HH:MM:SS)
      if (offset) {
        const startTime = offset.split('-')[0]; // Get first part: "00:00:37"
        const timeParts = startTime.split(':');
        const seconds = parseInt(timeParts[0]) * 3600 + parseInt(timeParts[1]) * 60 + parseInt(timeParts[2]);
        presignedUrl += `#t=${seconds}`;
      }
      
      window.open(presignedUrl, '_blank');
    } catch (error) {
      console.error('Failed to get presigned URL for video:', error);
    } finally {
      this.videoLoadingStates[index] = false;
      this.cdRef.detectChanges();
    }
  }

  async saveReplyAsPDF(): Promise<void> {
    this.isLoading = true;
    this.cdRef.detectChanges();

    // Cache for PDF documents by URL
    const pdfDocCache: Map<string, pdfjsLib.PDFDocumentProxy> = new Map();

    try {
      const pdf = new jsPDF();
      pdf.setFontSize(16);
      pdf.text('Equipment Details', 10, 10);
      pdf.setFontSize(12);

      let yPos = 20;
      if (this.equipmentDetails.id) { pdf.text(`ID: ${this.equipmentDetails.id}`, 10, yPos); yPos += 10; }
      if (this.equipmentDetails.make) { pdf.text(`Make: ${this.equipmentDetails.make}`, 10, yPos); yPos += 10; }
      if (this.equipmentDetails.model) { pdf.text(`Model: ${this.equipmentDetails.model}`, 10, yPos); yPos += 10; }
      if (this.equipmentDetails.serialNumberRange) { pdf.text(`Serial Number Range: ${this.equipmentDetails.serialNumberRange}`, 10, yPos); yPos += 10; }
      if (this.equipmentDetails.pvc) { pdf.text(`PVC: ${this.equipmentDetails.pvc}`, 10, yPos); yPos += 10; }
      if (this.equipmentDetails.equipmentCode) { pdf.text(`Equipment Code: ${this.equipmentDetails.equipmentCode}`, 10, yPos); yPos += 10; }

      pdf.addPage();
      pdf.setFontSize(16);
      pdf.text('Question and Answer', 10, 10);
      pdf.setFontSize(12);
      pdf.text(`Q: ${this.message}`, 10, 30, { maxWidth: 190 });
      pdf.text(`A: ${this.reply}`, 10, 45, { maxWidth: 190 });

      pdf.addPage();

      // Process resources sequentially to handle page labels properly
      for (let i = 0; i < this.resources.length; i++) {
        const resource = this.resources[i];
        const pdfUrl = resource.downloadUrl;
        const originalPdfUrl = pdfUrl.replace(/%3B/g, ';');
        const pageString = resource.page?.toString()!;
        const urlPdfPathName = new URL(originalPdfUrl).pathname;
        let pdfObjectUrl: string | undefined;

        // Create a cache key based on the URL
        const cacheKey = pdfObjectUrl ?? originalPdfUrl;
        let pdfDocument: pdfjsLib.PDFDocumentProxy;

        // Check if PDF is already loaded in our cache
        if (pdfDocCache.has(cacheKey)) {
          pdfDocument = pdfDocCache.get(cacheKey)!;
        } else {
          // Load from local cache or fetch from URL
          const pdfCached = await this.localForage.get<any>(urlPdfPathName);
          if(pdfCached) {
            pdfObjectUrl = URL.createObjectURL(pdfCached);
          }

          // Create and store document in cache
          const doc = pdfjsLib.getDocument(pdfObjectUrl ?? originalPdfUrl);
          pdfDocument = await doc.promise;
          pdfDocCache.set(cacheKey, pdfDocument);
        }

        const pageLabels = await pdfDocument.getPageLabels() || [];
        // Determine the page to render
        let pageToRender = 1; // Default to first page

        if (resource.page !== undefined) {
          // If it's a string label, look for it in the pageLabels array
          const labelIndex = pageLabels.findIndex(label => label === pageString);
          if (labelIndex >= 0) {
            pageToRender = labelIndex + 1; // Found label, convert to page number (1-based)
          } else {
            // Try to parse as a number if not found as a label
            const parsedPage = parseInt(pageString, 10);
            if (!isNaN(parsedPage) && parsedPage > 0 && parsedPage <= pdfDocument.numPages) {
              pageToRender = parsedPage;
            }
          }
        }

        // Get the pages to render (previous, current, next)
        const pagesToRender = [pageToRender - 1, pageToRender, pageToRender + 1].filter(page => page > 0 && page <= pdfDocument.numPages);

        for (const pageIndex of pagesToRender) {
          const page = await pdfDocument.getPage(pageIndex);
          const viewport = page.getViewport({ scale: 1.5 });

          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          canvas.height = viewport.height;
          canvas.width = viewport.width;

          if (ctx) {
            await page.render({ canvasContext: ctx, viewport: viewport }).promise;

            // Get current page label for display
            const currentLabel = pageLabels[pageIndex - 1] || String(pageIndex);

             // Log information for debugging
             console.log(`Rendering resource ${i+1}:`);
             console.log(`- Requested page/label: ${resource.page}`);
             console.log(`- Physical page rendered: ${pageIndex}`);
             console.log(`- Page label: ${currentLabel}`);

            // Add a new page for each resource after the first one
            if (i > 0 || pageIndex !== pagesToRender[0]) pdf.addPage();

            // Add resource information to the PDF
            const messageWithoutPageReference = resource.message.replace(/^.*for:\s*/, '');
            pdf.text(messageWithoutPageReference, 10, 10, { maxWidth: 190 });
            pdf.addImage(canvas.toDataURL('image/jpeg'), 'JPEG', 0, 30, 210, 277 - 30);
          } else {
            throw new Error('Canvas rendering context is null');
          }
        }
      }

      await new Promise<void>((resolve) => {
        pdf.save('answer_export.pdf');
        setTimeout(() => resolve(), 100);
      });

    } catch (error) {
      console.error('Error generating PDF', error);
    } finally {
      this.isLoading = false;
      this.cdRef.detectChanges();
    }
  }

  openLinksInNewTab(): void {
    if (!this.genericReplyContainer) return;
    this.genericReplyContainer.nativeElement.querySelectorAll('a').forEach(link => {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    });
  }

  submitAnother(): void {
    this.onClick.emit();
  }

  tryGeneralAI(): void {
    this.onTryGeneralAI.emit();
  }

  formatMessage(message: string | null): string {
    if (!message) return '';
    const documentPattern = /<reference>\s*<document_name>(.*?)<\/document_name>\s*<page>(.*?)<\/page>\s*<\/reference>/;
    const match = message.match(documentPattern);
    if (match) {
      const documentInfo = {
        documentName: match[1].trim(),
        page: match[2].trim()  // Keep as string to handle labels
      };
    }
    return message.replace(/<complete\/>/g, '');
  }
}
